import { Box, styled } from '@mui/material';
import type { FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import type { AssistantMessage, BaseChatMessage, Conversation } from '@/src/shared/types';
import { CardType, ChatMessageRole } from '@/src/shared/types';

import {
    AssistantMessageCard,
    FeedbackConfirmedCard,
    FeedbackFormCard,
    UserMessageCard,
} from './Cards';

const CardTypeToComponentMap: Record<CardType, FC<any>> = {
    [CardType.FeedbackFormCard]: FeedbackFormCard,
    [CardType.FeedbackConfirmedCard]: FeedbackConfirmedCard,
};

const MessageCard = ({ message }: { message: BaseChatMessage }) => {
    if (message.role === ChatMessageRole.USER) {
        return <UserMessageCard message={message} />;
    }

    const assistantMessage = message as AssistantMessage;
    return (
        <>
            <AssistantMessageCard message={assistantMessage} />
            {assistantMessage.additionalCards.map((card) => {
                const CardComponent = CardTypeToComponentMap[card.cardType];
                return <CardComponent key={card.activityId} {...card.cardProps} />;
            })}
        </>
    );
};

const FlowBox = styled(Box)(({ theme }) => ({
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    overflowY: 'auto',
    '@media(max-width: 1024px)': {
        marginTop: '140px',
    },
    '& > .chat-flow-card': {
        width: '80%',
        fontFamily: 'Equip_Regular',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
    },
    '& img,video': {
        maxWidth: '100%',
    },
    '& table': {
        borderCollapse: 'collapse',
        '& th,td': {
            textAlign: 'left',
            border: `1px solid ${theme.palette.divider}`,
        },
    },
}));

interface ChatFlowProps {
    conversation: Conversation;
}

export const ChatFlow = ({ conversation }: ChatFlowProps) => {
    const scrollIndicatorRef = useRef<HTMLDivElement>(null);
    const lastMessage = useMemo(() => conversation.messages.at(-1), [conversation]);

    useEffect(() => {
        if (!lastMessage) {
            return;
        }
        const scrollIndicator = scrollIndicatorRef.current;
        if (!scrollIndicator) {
            return;
        }

        setTimeout(() => {
            scrollIndicator.scrollIntoView({ behavior: 'smooth' });
        }, 0);
    }, [lastMessage]);

    return (
        <FlowBox>
            {conversation.messages.map((message) => (
                <MessageCard key={message.activityId} message={message} />
            ))}
            <div ref={scrollIndicatorRef} className="scroll-indicator" />
        </FlowBox>
    );
};
